import { createTheme, PaletteMode } from "@mui/material/styles";

// Extend MUI theme to hold per-palette gradient strings and display font
declare module "@mui/material/styles" {
  interface Theme {
    gradients: {
      hero: string;
      auth: string;
      contentBg: string;
    };
    fonts: {
      display: string;
    };
  }
  interface ThemeOptions {
    gradients?: {
      hero?: string;
      auth?: string;
      contentBg?: string;
    };
    fonts?: {
      display?: string;
    };
  }
}

export type PaletteKey =
  | "orangeCharcoal"
  | "navyAmber"
  | "purpleGold"
  | "crimsonSteel"
  | "electricIndigo";

interface PaletteVariant {
  label: string;
  swatchPrimary: string;
  swatchSecondary: string;
  displayFont: string;
  primaryMain: string;
  primaryLight: string;
  primaryDark: string;
  primaryDarkMode: string;
  secondaryMain: string;
  secondaryLight: string;
  secondaryDark: string;
  secondaryContrastText: string;
  bgDefault: string;
  bgPaper: string;
  bgDefaultDark: string;
  bgPaperDark: string;
  heroGradient: string;
  heroGradientDark: string;
  authGradient: string;
  authGradientDark: string;
  contentBgLight: string;
  contentBgDark: string;
}

export const paletteVariants: Record<PaletteKey, PaletteVariant> = {
  orangeCharcoal: {
    label: "Basketball Orange",
    swatchPrimary: "#BF360C",
    swatchSecondary: "#F9A825",
    displayFont: "'Bebas Neue', 'Arial Black', sans-serif",
    primaryMain: "#BF360C",
    primaryLight: "#E64A19",
    primaryDark: "#870000",
    primaryDarkMode: "#FF7043",
    secondaryMain: "#F9A825",
    secondaryLight: "#FFD74B",
    secondaryDark: "#C17900",
    secondaryContrastText: "#000000",
    bgDefault: "#FFF8F2",
    bgPaper: "#FFFCF9",
    bgDefaultDark: "#1C1C1E",
    bgPaperDark: "#2C2C2E",
    heroGradient:
      "linear-gradient(135deg, #BF360C 0%, #E64A19 55%, #F9A825 100%)",
    heroGradientDark:
      "linear-gradient(135deg, #3D0C02 0%, #BF360C 75%, #C17900 100%)",
    authGradient:
      "linear-gradient(135deg, #870000 0%, #BF360C 50%, #E64A19 100%)",
    authGradientDark: "linear-gradient(135deg, #1A0500 0%, #BF360C 100%)",
    contentBgLight:
      "radial-gradient(ellipse at 15% 25%, rgba(191,54,12,0.12) 0%, transparent 55%), " +
      "radial-gradient(ellipse at 85% 75%, rgba(249,168,37,0.10) 0%, transparent 55%), " +
      "radial-gradient(ellipse at 75% 15%, rgba(230,74,25,0.08) 0%, transparent 45%), " +
      "radial-gradient(ellipse at 40% 85%, rgba(249,168,37,0.06) 0%, transparent 40%), " +
      "linear-gradient(0deg, #FFF8F2, #FFF8F2)",
    contentBgDark:
      "radial-gradient(ellipse at 15% 25%, rgba(255,112,67,0.18) 0%, transparent 55%), " +
      "radial-gradient(ellipse at 85% 75%, rgba(249,168,37,0.14) 0%, transparent 55%), " +
      "radial-gradient(ellipse at 75% 15%, rgba(255,112,67,0.10) 0%, transparent 45%), " +
      "radial-gradient(ellipse at 40% 85%, rgba(249,168,37,0.08) 0%, transparent 40%), " +
      "linear-gradient(0deg, #1C1C1E, #1C1C1E)",
  },
  navyAmber: {
    label: "Navy & Amber",
    swatchPrimary: "#1A237E",
    swatchSecondary: "#FFA000",
    displayFont: "'Pacifico', cursive",
    primaryMain: "#1A237E",
    primaryLight: "#534bae",
    primaryDark: "#000051",
    primaryDarkMode: "#7986CB",
    secondaryMain: "#FFA000",
    secondaryLight: "#ffd149",
    secondaryDark: "#c67100",
    secondaryContrastText: "#000000",
    bgDefault: "#F5F5F5",
    bgPaper: "#FFFFFF",
    bgDefaultDark: "#121212",
    bgPaperDark: "#1E1E1E",
    heroGradient:
      "linear-gradient(135deg, #1A237E 0%, #283593 55%, #FFA000 100%)",
    heroGradientDark:
      "linear-gradient(135deg, #0D1340 0%, #1A237E 75%, #c67100 100%)",
    authGradient:
      "linear-gradient(135deg, #000051 0%, #1A237E 50%, #283593 100%)",
    authGradientDark: "linear-gradient(135deg, #07091E 0%, #1A237E 100%)",
    contentBgLight:
      "radial-gradient(ellipse at 15% 25%, rgba(26,35,126,0.14) 0%, transparent 55%), " +
      "radial-gradient(ellipse at 85% 75%, rgba(255,160,0,0.12) 0%, transparent 55%), " +
      "radial-gradient(ellipse at 75% 15%, rgba(83,75,174,0.09) 0%, transparent 45%), " +
      "radial-gradient(ellipse at 40% 85%, rgba(255,160,0,0.07) 0%, transparent 40%), " +
      "linear-gradient(0deg, #F5F5F5, #F5F5F5)",
    contentBgDark:
      "radial-gradient(ellipse at 15% 25%, rgba(121,134,203,0.18) 0%, transparent 55%), " +
      "radial-gradient(ellipse at 85% 75%, rgba(255,160,0,0.14) 0%, transparent 55%), " +
      "radial-gradient(ellipse at 75% 15%, rgba(121,134,203,0.10) 0%, transparent 45%), " +
      "radial-gradient(ellipse at 40% 85%, rgba(255,160,0,0.08) 0%, transparent 40%), " +
      "linear-gradient(0deg, #121212, #121212)",
  },
  purpleGold: {
    label: "Purple & Gold",
    swatchPrimary: "#4A148C",
    swatchSecondary: "#FFD600",
    displayFont: "'Cinzel', 'Times New Roman', serif",
    primaryMain: "#4A148C",
    primaryLight: "#7B1FA2",
    primaryDark: "#12005E",
    primaryDarkMode: "#AB47BC",
    secondaryMain: "#FFD600",
    secondaryLight: "#FFFF52",
    secondaryDark: "#C7A500",
    secondaryContrastText: "#000000",
    bgDefault: "#F3E5F5",
    bgPaper: "#FAF5FF",
    bgDefaultDark: "#1A0A2E",
    bgPaperDark: "#2A1A3E",
    heroGradient:
      "linear-gradient(135deg, #4A148C 0%, #7B1FA2 55%, #FFD600 100%)",
    heroGradientDark:
      "linear-gradient(135deg, #0D0018 0%, #4A148C 75%, #C7A500 100%)",
    authGradient:
      "linear-gradient(135deg, #12005E 0%, #4A148C 50%, #7B1FA2 100%)",
    authGradientDark: "linear-gradient(135deg, #08000F 0%, #4A148C 100%)",
    contentBgLight:
      "radial-gradient(ellipse at 15% 25%, rgba(74,20,140,0.14) 0%, transparent 55%), " +
      "radial-gradient(ellipse at 85% 75%, rgba(255,214,0,0.12) 0%, transparent 55%), " +
      "radial-gradient(ellipse at 75% 15%, rgba(123,31,162,0.09) 0%, transparent 45%), " +
      "radial-gradient(ellipse at 40% 85%, rgba(255,214,0,0.07) 0%, transparent 40%), " +
      "linear-gradient(0deg, #F3E5F5, #F3E5F5)",
    contentBgDark:
      "radial-gradient(ellipse at 15% 25%, rgba(171,71,188,0.20) 0%, transparent 55%), " +
      "radial-gradient(ellipse at 85% 75%, rgba(255,214,0,0.16) 0%, transparent 55%), " +
      "radial-gradient(ellipse at 75% 15%, rgba(171,71,188,0.12) 0%, transparent 45%), " +
      "radial-gradient(ellipse at 40% 85%, rgba(255,214,0,0.10) 0%, transparent 40%), " +
      "linear-gradient(0deg, #1A0A2E, #1A0A2E)",
  },
  crimsonSteel: {
    label: "Crimson & Steel",
    swatchPrimary: "#B71C1C",
    swatchSecondary: "#1565C0",
    displayFont: "'Oswald', 'Arial Narrow', sans-serif",
    primaryMain: "#B71C1C",
    primaryLight: "#C62828",
    primaryDark: "#7F0000",
    primaryDarkMode: "#EF5350",
    secondaryMain: "#1565C0",
    secondaryLight: "#5E92F3",
    secondaryDark: "#003C8F",
    secondaryContrastText: "#ffffff",
    bgDefault: "#FAFAFA",
    bgPaper: "#FFFFFF",
    bgDefaultDark: "#0D1B2A",
    bgPaperDark: "#1A2B3C",
    heroGradient:
      "linear-gradient(135deg, #B71C1C 0%, #C62828 45%, #1565C0 100%)",
    heroGradientDark:
      "linear-gradient(135deg, #060B13 0%, #B71C1C 65%, #003C8F 100%)",
    authGradient:
      "linear-gradient(135deg, #7F0000 0%, #B71C1C 50%, #C62828 100%)",
    authGradientDark: "linear-gradient(135deg, #040810 0%, #B71C1C 100%)",
    contentBgLight:
      "radial-gradient(ellipse at 15% 25%, rgba(183,28,28,0.12) 0%, transparent 55%), " +
      "radial-gradient(ellipse at 85% 75%, rgba(21,101,192,0.10) 0%, transparent 55%), " +
      "radial-gradient(ellipse at 75% 15%, rgba(198,40,40,0.08) 0%, transparent 45%), " +
      "radial-gradient(ellipse at 40% 85%, rgba(21,101,192,0.07) 0%, transparent 40%), " +
      "linear-gradient(0deg, #FAFAFA, #FAFAFA)",
    contentBgDark:
      "radial-gradient(ellipse at 15% 25%, rgba(239,83,80,0.18) 0%, transparent 55%), " +
      "radial-gradient(ellipse at 85% 75%, rgba(94,146,243,0.16) 0%, transparent 55%), " +
      "radial-gradient(ellipse at 75% 15%, rgba(239,83,80,0.10) 0%, transparent 45%), " +
      "radial-gradient(ellipse at 40% 85%, rgba(94,146,243,0.08) 0%, transparent 40%), " +
      "linear-gradient(0deg, #0D1B2A, #0D1B2A)",
  },
  electricIndigo: {
    label: "Electric Indigo",
    swatchPrimary: "#311B92",
    swatchSecondary: "#76FF03",
    displayFont: "'Orbitron', 'Courier New', monospace",
    primaryMain: "#311B92",
    primaryLight: "#6746C3",
    primaryDark: "#000063",
    primaryDarkMode: "#7C4DFF",
    secondaryMain: "#76FF03",
    secondaryLight: "#B0FF57",
    secondaryDark: "#32CB00",
    secondaryContrastText: "#000000",
    bgDefault: "#F0F0FF",
    bgPaper: "#F5F5FF",
    bgDefaultDark: "#0A0A1A",
    bgPaperDark: "#141428",
    heroGradient:
      "linear-gradient(135deg, #311B92 0%, #4527A0 55%, #76FF03 100%)",
    heroGradientDark:
      "linear-gradient(135deg, #050510 0%, #311B92 75%, #32CB00 100%)",
    authGradient:
      "linear-gradient(135deg, #000063 0%, #311B92 50%, #4527A0 100%)",
    authGradientDark: "linear-gradient(135deg, #030308 0%, #311B92 100%)",
    contentBgLight:
      "radial-gradient(ellipse at 15% 25%, rgba(49,27,146,0.14) 0%, transparent 55%), " +
      "radial-gradient(ellipse at 85% 75%, rgba(118,255,3,0.12) 0%, transparent 55%), " +
      "radial-gradient(ellipse at 75% 15%, rgba(103,70,195,0.09) 0%, transparent 45%), " +
      "radial-gradient(ellipse at 40% 85%, rgba(118,255,3,0.07) 0%, transparent 40%), " +
      "linear-gradient(0deg, #F0F0FF, #F0F0FF)",
    contentBgDark:
      "radial-gradient(ellipse at 15% 25%, rgba(124,77,255,0.22) 0%, transparent 55%), " +
      "radial-gradient(ellipse at 85% 75%, rgba(118,255,3,0.18) 0%, transparent 55%), " +
      "radial-gradient(ellipse at 75% 15%, rgba(124,77,255,0.14) 0%, transparent 45%), " +
      "radial-gradient(ellipse at 40% 85%, rgba(118,255,3,0.10) 0%, transparent 40%), " +
      "linear-gradient(0deg, #0A0A1A, #0A0A1A)",
  },
};

export const getTheme = (paletteKey: PaletteKey, mode: PaletteMode) => {
  const v = paletteVariants[paletteKey];
  const isDark = mode === "dark";

  return createTheme({
    palette: {
      mode,
      primary: {
        main: isDark ? v.primaryDarkMode : v.primaryMain,
        light: v.primaryLight,
        dark: v.primaryDark,
        contrastText: "#ffffff",
      },
      secondary: {
        main: v.secondaryMain,
        light: v.secondaryLight,
        dark: v.secondaryDark,
        contrastText: v.secondaryContrastText,
      },
      background: {
        default: isDark ? v.bgDefaultDark : v.bgDefault,
        paper: isDark ? v.bgPaperDark : v.bgPaper,
      },
      success: { main: "#2e7d32" },
      error: { main: "#c62828" },
    },
    gradients: {
      hero: isDark ? v.heroGradientDark : v.heroGradient,
      auth: isDark ? v.authGradientDark : v.authGradient,
      contentBg: isDark ? v.contentBgDark : v.contentBgLight,
    },
    fonts: {
      display: v.displayFont,
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h1: { fontWeight: 700 },
      h2: { fontWeight: 700 },
      h3: { fontWeight: 600 },
      h4: { fontWeight: 600 },
      h5: { fontWeight: 500 },
      h6: { fontWeight: 500 },
    },
    shape: { borderRadius: 8 },
    components: {
      MuiButton: {
        styleOverrides: {
          root: { textTransform: "none", fontWeight: 600 },
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 4 },
      },
    },
  });
};
